export const formatCultureHubDate = (date) => {
  if (!date) {
    return "Sem data";
  }

  const [year, month, day] = date.split("T")[0].split("-");

  return `${day}/${month}/${year.slice(2)}`;
};

const normalizeAttachment = (
  attachment,
  planningDate = null
) => ({
  ...attachment,
  cultureHubReleaseDate:
    attachment.release?.actionDate ?? null,
  diversityAssessmentPlanningDate:
    planningDate ?? null,
});

const buildChapterSection = (block) => ({
  id: `Chapter-${block.number}`,
  kind: "Chapter",
  blockNumber: block.number,

  attachments: (block.chapters ?? []).flatMap((chapter) =>
    (chapter.attachments ?? []).map((attachment) => ({
      ...normalizeAttachment(
        attachment,
        block.diversityAssessmentPlanningDate
      ),
      number: chapter.number,
    }))
  ),
});

const buildMaterialSection = (material, index) => ({
  id: `OtherType-${material.typeName ?? index}`,
  kind: "OtherType",
  typeName: material.typeName,

  attachments: (material.attachments ?? []).map(
    (attachment) => ({
      ...normalizeAttachment(
        attachment,
        material.diversityAssessmentPlanningDate
      ),

      type: (attachment.type ?? material.typeName)
        ?.split("/")[0]
        .trim(),
    })
  ),
});

const buildEpisodeSection = (material, index) => ({
  id: `Episode-${material.episodeNumber ?? index}`,
  kind: "Episode",
  episodeNumber: material.episodeNumber,

  attachments: (material.attachments ?? []).map(
    (attachment) => ({
      ...normalizeAttachment(
        attachment,
        material.diversityAssessmentPlanningDate
      ),

      number: material.episodeNumber,
    })
  ),
});

const buildAddendumSection = (addenda = []) => ({
  id: "Addendum",
  kind: "Addendum",
  typeName: "Adendo",

  attachments: addenda.map((attachment) =>
    normalizeAttachment(attachment)
  ),
});

/**
 * Retorna todos os IDs dos attachments pertencentes
 * aos capítulos de um bloco.
 */
const getBlockAttachmentIds = (block) =>
  (block.chapters ?? []).flatMap((chapter) =>
    (chapter.attachments ?? []).map(
      (attachment) => attachment.id
    )
  );

/**
 * Encontra os pareceres relacionados aos attachments
 * pertencentes a um determinado bloco.
 *
 * assessment.related[].id
 *          ↓
 * block.chapters[].attachments[].id
 */
const getAssessmentsForBlock = (
  block,
  assessments = []
) => {
  const blockAttachmentIds = new Set(
    getBlockAttachmentIds(block)
  );

  return assessments.filter((assessment) =>
    assessment.related?.some((related) =>
      blockAttachmentIds.has(related.id)
    )
  );
};

/**
 * Converte o retorno do endpoint de Assessments
 * para o formato utilizado pelas tabelas.
 *
 * IMPORTANTE:
 * O `id` aqui é o ID REAL DO ANEXO DO PARECER.
 *
 * assessment.id
 *      = ID do registro do parecer
 *
 * assessment.reportAttachmentId
 *      = ID do arquivo/anexo do parecer
 */
const buildAssessmentAttachments = (
  assessments = [],
  isEpisode = false
) =>
  assessments.map((assessment) => {
    const relatedChapterEpisodes =
      assessment.related?.filter(
        (related) =>
          related.typeName === "Capítulo/Episódio"
      ) ?? [];

    const relatedNumbers =
      relatedChapterEpisodes
        .map((related) => {
          const match =
            related.description?.match(
              /(?:Capítulo|Episódio)\s+(\d+)/i
            );

          return match ? Number(match[1]) : null;
        })
        .filter(Boolean);

    return {
      // ID REAL DO ARQUIVO
      id: assessment.reportAttachmentId,

      // Mantemos o ID do registro do parecer separadamente
      assessmentId: assessment.id,

      reportAttachmentId:
        assessment.reportAttachmentId,

      fileName: assessment.fileName,

      version:
        relatedChapterEpisodes[0]?.version ?? null,

      relatedNumbers,

      isEpisode,

      createdAt: assessment.createdAt,

      cultureHubReleaseDate:
        assessment.createdAt,
    };
  });

export const buildCultureHubGroups = (
  data = {},
  assessments = []
) => {
  const {
    blocks = [],
    materials = [],
    addenda = [],
  } = data;

  const groups = [];

  const episodeMaterials = materials.filter(
    (material) =>
      material.typeName === "Capítulo/Episódio"
  );

  const otherMaterials = materials.filter(
    (material) =>
      material.typeName !== "Capítulo/Episódio"
  );

  /**
   * OUTROS MATERIAIS
   */
  if (otherMaterials.length > 0) {
    const sections = otherMaterials.map(
      buildMaterialSection
    );

    const otherMaterialAssessments =
      assessments.filter((assessment) =>
        assessment.related?.some((related) =>
          otherMaterials.some(
            (material) =>
              material.typeName === related.typeName
          )
        )
      );

    if (otherMaterialAssessments.length > 0) {
      sections.push({
        id: "Assessment-other-types",
        kind: "Assessment",
        typeName: "Parecer",
        attachments:
          buildAssessmentAttachments(
            otherMaterialAssessments
          ),
      });
    }

    groups.push({
      id: "other-types",
      kind: "OtherType",
      label: "Outros Materiais",

      count: otherMaterials.reduce(
        (total, material) =>
          total + (material.attachments?.length ?? 0),
        0
      ),

      assessmentCount:
        otherMaterialAssessments.length,

      sections,
    });
  }

  /**
   * BLOCOS / CAPÍTULOS
   */
  blocks.forEach((block) => {
    const chapterSection =
      buildChapterSection(block);

    const blockAssessments =
      getAssessmentsForBlock(
        block,
        assessments
      );

    const sections = [chapterSection];

    if (blockAssessments.length > 0) {
      sections.push({
        id: `Assessment-block-${block.number}`,
        kind: "Assessment",
        blockNumber: block.number,
        typeName: "Parecer",

        attachments:
          buildAssessmentAttachments(
            blockAssessments
          ),
      });
    }

    groups.push({
      id: `block-${block.number}`,
      kind: "Chapter",
      label: `Bloco ${block.number}`,

      count:
        chapterSection.attachments.length,

      assessmentCount:
        blockAssessments.length,

      sections,
    });
  });

  /**
   * EPISÓDIOS
   */
  if (episodeMaterials.length > 0) {
    const sections = episodeMaterials.map(
      buildEpisodeSection
    );

    const episodeAssessments =
      assessments.filter((assessment) =>
        assessment.related?.some(
          (related) =>
            related.typeName ===
            "Capítulo/Episódio"
        )
      );

    if (episodeAssessments.length > 0) {
      sections.push({
        id: "Assessment-episodes",
        kind: "Assessment",
        typeName: "Parecer",

        attachments:
          buildAssessmentAttachments(
            episodeAssessments,
            true
          ),
      });
    }

    groups.push({
      id: "episodes",
      kind: "Episode",
      label: "Episódios",

      count: episodeMaterials.reduce(
        (total, material) =>
          total +
          (material.attachments?.length ?? 0),
        0
      ),

      assessmentCount:
        episodeAssessments.length,

      sections,
    });
  }

  /**
   * ADENDOS
   */
  if (addenda.length > 0) {
    const section =
      buildAddendumSection(addenda);

    const addendumAssessments =
      assessments.filter((assessment) =>
        assessment.related?.some((related) =>
          addenda.some(
            (addendum) =>
              addendum.id === related.id &&
              related.typeName === "Adendo"
          )
        )
      );

    const sections = [section];

    if (addendumAssessments.length > 0) {
      sections.push({
        id: "Assessment-addendums",
        kind: "Assessment",
        typeName: "Parecer",

        attachments:
          buildAssessmentAttachments(
            addendumAssessments
          ),
      });
    }

    groups.push({
      id: "addendums",
      kind: "Addendum",
      label: "Adendos",

      count: addenda.length,

      assessmentCount:
        addendumAssessments.length,

      sections,
    });
  }

  return groups;
};

// const buildAssessmentAttachments = (
//   assessments = [],
//   isEpisode = false
// ) =>
//   assessments.map((assessment) => {
//     const relatedChapterEpisodes =
//       assessment.related?.filter(
//         (related) =>
//           related.typeName === "Capítulo/Episódio"
//       ) ?? [];

//     const relatedNumbers =
//       relatedChapterEpisodes
//         .map((related) => {
//           const match =
//             related.description?.match(
//               /(?:Capítulo|Episódio)\s+(\d+)/i
//             );

//           return match ? Number(match[1]) : null;
//         })
//         .filter(Boolean);

//     return {
//       // IMPORTANTE:
//       // o ID visual do parecer precisa ser diferente
//       // do ID do attachment relacionado
//       id: `assessment-${assessment.id}`,

//       // Mantemos o ID real do report para usar
//       // quando for necessário enviar o parecer
//       reportAttachmentId:
//         assessment.reportAttachmentId,

//       fileName: assessment.fileName,

//       version:
//         relatedChapterEpisodes[0]?.version ?? null,

//       relatedNumbers,

//       isEpisode,

//       createdAt: assessment.createdAt,

//       cultureHubReleaseDate:
//         assessment.createdAt,
//     };
//   });

export const getAllAttachmentIds = (
  groups = []
) =>
  groups.flatMap((group) =>
    group.sections.flatMap((section) =>
      section.attachments.map(
        (attachment) => attachment.id
      )
    )
  );